
import etiquetasService from "@/modules/Etiquetas/services/etiquetasService";
import {
  clone,
  previewValue,
  priceDisplayLines,
  resolveElementForPrintOptions,
  resolveElementsForPrintOptions,
  resolvePreviewValues,
  shouldDisplayPreview,
} from "@/modules/Etiquetas/services/etiquetaDefinition";

const codeAssetCache = new Map();
const logoAssetCache = new Map();
const MAX_CODE_ASSETS = 60;

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const rememberAsset = (cache, key, promise, maximum = MAX_CODE_ASSETS) => {
  if (cache.size >= maximum) cache.delete(cache.keys().next().value);
  cache.set(key, promise);
  promise.catch(() => cache.delete(key));
  return promise;
};

export default {
  name: "EtiquetaLabelCanvas",
  props: {
    definition: {
      type: Object,
      required: true,
    },
    sampleValues: {
      type: Object,
      default: () => ({}),
    },
    selectedId: {
      type: String,
      default: null,
    },
    zoom: {
      type: Number,
      default: 100,
    },
    showGrid: {
      type: Boolean,
      default: true,
    },
    testOptions: {
      type: Object,
      default: () => ({}),
    },
    exampleCompanyId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      interaction: null,
      codeAssets: {},
      codeAssetErrors: {},
      codeAssetTimer: null,
      codeAssetGeneration: 0,
      logoUrl: "",
      companyValues: {},
      logoGeneration: 0,
    };
  },
  computed: {
    page() {
      return this.definition.pagina || {};
    },
    label() {
      return this.definition.etiqueta || {};
    },
    labelWidth() {
      return Number(this.label.largura_mm || 50);
    },
    labelHeight() {
      return Number(this.label.altura_mm || 30);
    },
    scale() {
      return 5.2 * Math.max(0.5, Math.min(2, Number(this.zoom || 100) / 100));
    },
    resolvedValues() {
      return resolvePreviewValues(
        { ...this.sampleValues, ...this.companyValues },
        this.testOptions
      );
    },
    pageMargins() {
      return {
        top: Math.max(0, Number(this.page.margem_superior_mm || 0)),
        right: Math.max(0, Number(this.page.margem_direita_mm || 0)),
        bottom: Math.max(0, Number(this.page.margem_inferior_mm || 0)),
        left: Math.max(0, Number(this.page.margem_esquerda_mm || 0)),
      };
    },
    pageMarginSummary() {
      const margin = this.pageMargins;
      return `${this.formatMillimeters(margin.top)} / ${this.formatMillimeters(
        margin.right
      )} / ${this.formatMillimeters(margin.bottom)} / ${this.formatMillimeters(
        margin.left
      )} mm`;
    },
    canvasOffset() {
      return {
        left: 40 + this.pageMargins.left * this.scale,
        top: 30 + this.pageMargins.top * this.scale,
      };
    },
    pageMarginBands() {
      const margin = this.pageMargins;
      const left = 40;
      const top = 30;
      const totalWidth =
        (margin.left + this.labelWidth + margin.right) * this.scale;
      const labelHeight = this.labelHeight * this.scale;
      const labelLeft = this.canvasOffset.left;
      const labelTop = this.canvasOffset.top;

      return [
        {
          side: "top",
          size: margin.top,
          style: {
            left: `${left}px`,
            top: `${top}px`,
            width: `${totalWidth}px`,
            height: `${margin.top * this.scale}px`,
          },
        },
        {
          side: "right",
          size: margin.right,
          style: {
            left: `${labelLeft + this.labelWidth * this.scale}px`,
            top: `${labelTop}px`,
            width: `${margin.right * this.scale}px`,
            height: `${labelHeight}px`,
          },
        },
        {
          side: "bottom",
          size: margin.bottom,
          style: {
            left: `${left}px`,
            top: `${labelTop + labelHeight}px`,
            width: `${totalWidth}px`,
            height: `${margin.bottom * this.scale}px`,
          },
        },
        {
          side: "left",
          size: margin.left,
          style: {
            left: `${left}px`,
            top: `${labelTop}px`,
            width: `${margin.left * this.scale}px`,
            height: `${labelHeight}px`,
          },
        },
      ].filter((band) => band.size > 0);
    },
    orderedElements() {
      return [...(this.definition.elementos || [])].sort(
        (first, second) => Number(first.ordem || 0) - Number(second.ordem || 0)
      );
    },
    renderedElements() {
      const convertedLegacy = Boolean(
        this.definition?.importacao_legado?.codigo
      );
      return this.orderedElements.flatMap((source) => {
        const parts = resolveElementsForPrintOptions(
          source,
          this.testOptions,
          convertedLegacy
        );
        return parts.map((part, index) => ({
          ...part,
          __previewResolved: true,
          __sourceId: source.id,
          __partIndex: index,
          __partCount: parts.length,
        }));
      });
    },
    xMarks() {
      const step = this.labelWidth > 100 ? 20 : 10;
      return Array.from(
        { length: Math.floor(this.labelWidth / step) + 1 },
        (_, index) => index * step
      );
    },
    yMarks() {
      const step = this.labelHeight > 100 ? 20 : 10;
      return Array.from(
        { length: Math.floor(this.labelHeight / step) + 1 },
        (_, index) => index * step
      );
    },
    stageStyle() {
      return {
        width: `${
          (this.pageMargins.left + this.labelWidth + this.pageMargins.right) *
            this.scale +
          58
        }px`,
        height: `${
          (this.pageMargins.top + this.labelHeight + this.pageMargins.bottom) *
            this.scale +
          58
        }px`,
      };
    },
    canvasStyle() {
      return {
        left: `${this.canvasOffset.left}px`,
        top: `${this.canvasOffset.top}px`,
        width: `${this.labelWidth * this.scale}px`,
        height: `${this.labelHeight * this.scale}px`,
        backgroundColor: this.label.fundo || "#FFFFFF",
        borderColor: this.label.cor_borda || "#000000",
        borderWidth: `${Number(this.label.borda_mm || 0) * this.scale}px`,
        borderRadius: `${
          Number(this.label.cantos_arredondados_mm || 0) * this.scale
        }px`,
        backgroundSize: [
          `${this.scale / 2}px ${this.scale / 2}px`,
          `${this.scale / 2}px ${this.scale / 2}px`,
          `${this.scale * 5}px ${this.scale * 5}px`,
          `${this.scale * 5}px ${this.scale * 5}px`,
        ].join(", "),
      };
    },
    safeAreaStyle() {
      const padding = this.safePadding * this.scale;
      return {
        top: `${padding}px`,
        right: `${padding}px`,
        bottom: `${padding}px`,
        left: `${padding}px`,
        display: padding > 0 ? "block" : "none",
      };
    },
    safePadding() {
      return Math.max(
        0,
        Math.min(
          Number(this.label.padding_mm || 0),
          this.labelWidth / 2,
          this.labelHeight / 2
        )
      );
    },
    rulerXStyle() {
      return {
        left: `${this.canvasOffset.left}px`,
        top: `${this.canvasOffset.top - 19}px`,
        width: `${this.labelWidth * this.scale}px`,
      };
    },
    rulerYStyle() {
      return {
        left: `${this.canvasOffset.left - 21}px`,
        top: `${this.canvasOffset.top}px`,
        height: `${this.labelHeight * this.scale}px`,
      };
    },
    codeAssetSignature() {
      return JSON.stringify(
        this.renderedElements
          .filter((element) =>
            ["barcode", "qrcode"].includes(this.previewElementType(element))
          )
          .map((element) => ({
            id: element.id,
            tipo: this.previewElementType(element),
            valor: this.rawValue(element),
            largura_mm: element.largura_mm,
            altura_mm: element.altura_mm,
            codigo_barras: element.codigo_barras || {},
            mostrar: this.elementContentVisible(element),
          }))
      );
    },
  },
  watch: {
    codeAssetSignature: {
      immediate: true,
      handler() {
        this.scheduleCodeAssets();
      },
    },
    exampleCompanyId: {
      immediate: true,
      handler() {
        this.loadLogo();
      },
    },
  },
  beforeDestroy() {
    if (this.codeAssetTimer) clearTimeout(this.codeAssetTimer);
    this.codeAssetGeneration += 1;
    this.logoGeneration += 1;
    this.removeDocumentListeners();
  },
  methods: {
    previewElement(element) {
      if (element?.__previewResolved) return element;
      return resolveElementForPrintOptions(
        element,
        this.testOptions,
        Boolean(this.definition?.importacao_legado?.codigo)
      );
    },
    previewElementType(element) {
      return this.previewElement(element)?.tipo || element.tipo;
    },
    elementClasses(element) {
      return {
        selected: this.selectedId === this.sourceElementId(element),
        hidden: element.visivel === false,
        "rule-hidden": !this.elementContentVisible(element),
        [`type-${element.tipo}`]: true,
      };
    },
    sourceElementId(element) {
      return element?.__sourceId || element?.id;
    },
    sourceElement(element) {
      const sourceId = this.sourceElementId(element);
      return (
        this.orderedElements.find((candidate) => candidate.id === sourceId) ||
        element
      );
    },
    isFirstRenderedPart(element) {
      return Number(element?.__partIndex || 0) === 0;
    },
    isLastRenderedPart(element) {
      return (
        Number(element?.__partIndex || 0) ===
        Number(element?.__partCount || 1) - 1
      );
    },
    elementStyle(element) {
      const style = element.estilo || {};
      const verticalMap = {
        top: "flex-start",
        middle: "center",
        bottom: "flex-end",
      };
      return {
        left: `${Number(element.x_mm || 0) * this.scale}px`,
        top: `${Number(element.y_mm || 0) * this.scale}px`,
        width: `${Number(element.largura_mm || 1) * this.scale}px`,
        height: `${Number(element.altura_mm || 1) * this.scale}px`,
        zIndex: Number(element.ordem || 0) + 2,
        overflow: "hidden",
        transform: `rotate(${Number(element.rotacao || 0)}deg)`,
        color: style.cor || "#000000",
        backgroundColor: style.fundo || "transparent",
        borderColor: style.cor_borda || "#000000",
        borderWidth: `${Number(style.borda_mm || 0) * this.scale}px`,
        padding: `${Number(style.padding_mm || 0) * this.scale}px`,
        fontFamily: this.fontFamily(style.fonte),
        fontSize: `${Number(style.tamanho_pt || 8) * 0.3528 * this.scale}px`,
        fontWeight: style.negrito ? 700 : 400,
        fontStyle: style.italico ? "italic" : "normal",
        textDecoration: style.sublinhado ? "underline" : "none",
        textAlign: style.alinhamento || "left",
        alignItems: verticalMap[style.alinhamento_vertical] || "center",
        letterSpacing: `${
          Number(style.espacamento_letras_pt || 0) * 0.3528 * this.scale
        }px`,
        whiteSpace: "normal",
        wordBreak: "normal",
        overflowWrap: "normal",
      };
    },
    linePreviewStyle(element) {
      const style = element.estilo || {};
      const requestedWidth = Number(style.borda_mm || 0.2);
      const widthMm = Math.max(
        0.1,
        Number.isFinite(requestedWidth) ? requestedWidth : 0.2
      );

      return {
        borderTop: `${widthMm * this.scale}px solid ${
          style.cor_borda || "#000000"
        }`,
      };
    },
    fontFamily(font) {
      return (
        {
          dejavu_serif:
            "'Etiqueta DejaVu Serif', 'DejaVu Serif Condensed', serif",
          dejavu_mono:
            "'Etiqueta DejaVu Sans Mono', 'DejaVu Sans Mono', monospace",
        }[font] || "'Etiqueta DejaVu Sans', 'DejaVu Sans', sans-serif"
      );
    },
    priceLines(element) {
      return priceDisplayLines(
        this.previewElement(element),
        this.resolvedValues
      );
    },
    priceLineStyle(element, line) {
      const style = element.estilo || {};
      const fontSize = Number(style.tamanho_pt || 8);
      const inset = Number(style.padding_mm || 0) + Number(style.borda_mm || 0);
      const width = Math.max(0.2, Number(element.largura_mm || 0) - 2 * inset);
      const estimatedWidth =
        Math.max(1, Array.from(line).length) * fontSize * 0.23;
      const lineCount = this.priceLines(element)?.length || 1;
      const height = Math.max(
        0.2,
        Number(element.altura_mm || 0) - 2 * inset - 0.5
      );
      const heightLimit = height / (lineCount * 0.3528 * 1.05);
      const fittedPt =
        Math.round(
          Math.max(
            4,
            Math.min(fontSize, (fontSize * width) / estimatedWidth, heightLimit)
          ) * 100
        ) / 100;
      return { fontSize: `${fittedPt * 0.3528 * this.scale}px` };
    },
    displayValue(element) {
      return previewValue(this.previewElement(element), this.resolvedValues);
    },
    rawValue(element) {
      const value = this.resolvedValues[this.previewElement(element)?.campo];
      return value === null || value === undefined ? "" : String(value);
    },
    elementContentVisible(element) {
      return shouldDisplayPreview(
        this.previewElement(element),
        this.resolvedValues,
        this.testOptions,
        this.logoUrl
      );
    },
    codeAssetUrl(element) {
      return this.codeAssets[element.id] || "";
    },
    codeAssetFailed(element) {
      return Boolean(this.codeAssetErrors[element.id]);
    },
    barcodeImageStyle(element) {
      const showText = Boolean(element.codigo_barras?.exibir_texto);
      return { maxHeight: showText ? "78%" : "100%" };
    },
    scheduleCodeAssets() {
      if (this.codeAssetTimer) clearTimeout(this.codeAssetTimer);
      this.codeAssetTimer = setTimeout(() => {
        this.codeAssetTimer = null;
        this.loadCodeAssets();
      }, 120);
    },
    async loadCodeAssets() {
      const generation = ++this.codeAssetGeneration;
      const elements = this.renderedElements.filter(
        (element) =>
          ["barcode", "qrcode"].includes(this.previewElementType(element)) &&
          this.elementContentVisible(element) &&
          this.rawValue(element) !== ""
      );
      const next = {};
      const errors = {};

      await Promise.all(
        elements.map(async (element) => {
          const payload = {
            tipo: this.previewElementType(element),
            valor: this.rawValue(element),
            largura_mm: Number(element.largura_mm),
            altura_mm: Number(element.altura_mm),
            codigo_barras: element.codigo_barras || {},
          };
          const key = JSON.stringify(payload);
          let asset = codeAssetCache.get(key);
          if (!asset) {
            asset = rememberAsset(
              codeAssetCache,
              key,
              etiquetasService
                .codigoImagem(payload)
                .then(({ data }) => blobToDataUrl(data))
            );
          }

          try {
            next[element.id] = await asset;
          } catch (error) {
            next[element.id] = "";
            errors[element.id] = true;
          }
        })
      );

      if (generation === this.codeAssetGeneration) {
        this.codeAssets = next;
        this.codeAssetErrors = errors;
      }
    },
    async loadLogo() {
      const generation = ++this.logoGeneration;
      const id = Number(this.exampleCompanyId || 0);
      this.logoUrl = "";
      this.companyValues = {};
      if (!id) return;

      let asset = logoAssetCache.get(id);
      if (!asset) {
        asset = rememberAsset(
          logoAssetCache,
          id,
          etiquetasService.logoImagem(id).then(({ data }) => ({
            url: String(data?.url || ""),
            values:
              data?.dados_etiqueta && typeof data.dados_etiqueta === "object"
                ? clone(data.dados_etiqueta)
                : {},
          })),
          30
        );
      }

      try {
        const companyAsset = await asset;
        if (generation === this.logoGeneration) {
          this.logoUrl = companyAsset.url;
          this.companyValues = companyAsset.values;
        }
      } catch (error) {
        if (generation === this.logoGeneration) {
          this.logoUrl = "";
          this.companyValues = {};
        }
      }
    },
    formatMillimeters(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    beginDrag(event, element) {
      const source = this.sourceElement(element);
      this.$emit("select", source.id);
      this.$emit("change-start");
      this.startInteraction("drag", event, source);
    },
    beginResize(event, element) {
      const source = this.sourceElement(element);
      this.$emit("select", source.id);
      this.$emit("change-start");
      this.startInteraction("resize", event, source);
    },
    startInteraction(type, event, element) {
      this.interaction = {
        type,
        startX: event.clientX,
        startY: event.clientY,
        element: clone(element),
      };
      document.addEventListener("mousemove", this.onPointerMove);
      document.addEventListener("mouseup", this.endInteraction);
    },
    onPointerMove(event) {
      if (!this.interaction) return;
      const source = this.interaction.element;
      const next = clone(source);
      const step = event.altKey ? 0.1 : 0.5;
      const deltaX = (event.clientX - this.interaction.startX) / this.scale;
      const deltaY = (event.clientY - this.interaction.startY) / this.scale;

      if (this.interaction.type === "drag") {
        const maxX = Math.max(0, this.labelWidth - Number(source.largura_mm));
        const maxY = Math.max(0, this.labelHeight - Number(source.altura_mm));
        next.x_mm = this.snap(
          Math.max(0, Math.min(maxX, Number(source.x_mm) + deltaX)),
          step
        );
        next.y_mm = this.snap(
          Math.max(0, Math.min(maxY, Number(source.y_mm) + deltaY)),
          step
        );
      } else {
        const maxWidth = Math.max(0.5, this.labelWidth - Number(source.x_mm));
        const maxHeight = Math.max(0.5, this.labelHeight - Number(source.y_mm));
        next.largura_mm = this.snap(
          Math.max(0.5, Math.min(maxWidth, Number(source.largura_mm) + deltaX)),
          step
        );
        next.altura_mm = this.snap(
          Math.max(0.5, Math.min(maxHeight, Number(source.altura_mm) + deltaY)),
          step
        );
      }
      this.$emit("update", next);
    },
    endInteraction() {
      this.interaction = null;
      this.removeDocumentListeners();
      this.$emit("change-end");
    },
    removeDocumentListeners() {
      document.removeEventListener("mousemove", this.onPointerMove);
      document.removeEventListener("mouseup", this.endInteraction);
    },
    snap(value, step) {
      return Number((Math.round(value / step) * step).toFixed(2));
    },
  },
};
